import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, styled, Typography, useTheme } from '@mui/material';
import { IArgument, IDineshTicketOperations, IPolicySet } from '@tymely/atoms';
import { useCreateTicketCrumb, useIntentsQuery, useSelectedComment, useTicketOfflineQuery } from '@tymely/services';
import { useQaStepperContext } from '@tymely/features/Ticket/QaMode/QaStepperContext';
import {
    useApproveQaMutation,
    useQaRejectMutation,
    useQaRejectRevertMutation,
} from '@tymely/features/Ticket/QaMode/services';
import { SearchAddIntent } from '@tymely/components/Intents/SearchAddIntent';
import { IntentsList } from '@tymely/components/Intents/IntentsList';
import { useDebounce } from '@tymely/utils';
import { containsHebrew, switchHebrewToEnglish } from '@tymely/components/Intents/QuertyMapping';
import { addProposedIntents, groupAndSortIntents, searchIntent } from '@tymely/utils/intents';
import { STEP_ARGUMENTS, STEP_RESOLUTION } from '@tymely/features/Ticket/QaMode/constants';
import { useAtomValue } from 'jotai';
import { selectedPolicySetIdAtom } from '@tymely/atoms/policySet.atoms';

const Root = styled(Box)({
    position: 'relative',
});

export const QaIntents = () => {
    const {
        setActiveStep,
        isArgQueryLoading,
        intentArg,
        setIsIntentChanged,
        setIntentArg,
        isIntentChanged,
        originalIntentArg,
        refetchArgs,
    } = useQaStepperContext();
    const theme = useTheme();
    const qaApproveMutation = useApproveQaMutation();
    const qaRejectMutation = useQaRejectMutation();
    const qaRejectRevertMutation = useQaRejectRevertMutation();
    const intents = useIntentsQuery();
    const ticketOfflineQuery = useTicketOfflineQuery(undefined, false);
    const [search, setSearch] = useState('');
    const [isSearchModified, setIsSearchModified] = useState(false);
    const selectedComment = useSelectedComment();
    const searchVal = useDebounce(search, 300) || '';
    const inferredIntents = selectedComment?.infer_data?.intents;
    const selectedPolicySetId = useAtomValue(selectedPolicySetIdAtom);
    const createTicketCrumb = useCreateTicketCrumb();

    const allIntentGroups = useMemo(
        () => groupAndSortIntents(addProposedIntents(intents.data ?? [], inferredIntents)),
        [intents.data, inferredIntents],
    );

    const intentGroups = useMemo(() => {
        if (!searchVal.length) {
            return allIntentGroups;
        }

        if (searchVal.length <= 1) {
            return [];
        }

        const searchTerm = containsHebrew(searchVal) ? switchHebrewToEnglish(searchVal) : searchVal;
        const foundIntents = addProposedIntents(searchIntent(intents.data ?? [], searchTerm), inferredIntents);
        return groupAndSortIntents(foundIntents);
    }, [searchVal, allIntentGroups, inferredIntents, selectedComment]);

    useEffect(() => {
        let name = '';
        if (intents.data) {
            if (selectedPolicySetId) {
                name = intents.data.find((intent) => intent.id === selectedPolicySetId)?.name || '';
            } else if (intentArg) {
                const id = ((intentArg as IArgument).value as number[])[0];
                name = intents.data.find((intent) => intent.id === id)?.name || '';
            }
        }
        setSearch(name);
        setIsSearchModified(false);
    }, [selectedPolicySetId, intents.dataUpdatedAt, intentArg]);

    const handleSearchChange = (newSearch: string) => {
        setSearch(newSearch);
        setIsSearchModified(true);
    };

    const handleQaApprove = useCallback(
        (isRevertRejection = false) => {
            if (originalIntentArg?.id) {
                if (isRevertRejection) {
                    qaRejectRevertMutation.mutate(originalIntentArg?.id);
                }
                createTicketCrumb(IDineshTicketOperations.QA_USER_APPROVED_PREDICTED_INTENT, {
                    intent_arg: originalIntentArg,
                });
                qaApproveMutation.mutate(originalIntentArg.id, {
                    onSuccess: () => {
                        setActiveStep(STEP_ARGUMENTS);
                        setIsIntentChanged(false);
                        refetchArgs();
                    },
                });
            }
        },
        [setActiveStep, originalIntentArg, qaApproveMutation],
    );

    const onIntentSelect = useCallback(
        (intent: IPolicySet) => {
            if (intent?.id !== ((originalIntentArg as IArgument)?.value as number[])?.[0]) {
                createTicketCrumb(IDineshTicketOperations.QA_USER_MODIFIED_PREDICTED_INTENT, {
                    intent_arg: originalIntentArg,
                    newValue: [intent.id],
                });
                qaRejectMutation.mutate(
                    { id: (originalIntentArg as IArgument).id, value: [intent.id] },
                    {
                        onSuccess: () => {
                            setIntentArg((prev) => {
                                if (!prev) return undefined;
                                return {
                                    ...prev,
                                    value: [intent.id],
                                } as IArgument;
                            });
                            setIsIntentChanged(true);
                            setActiveStep(STEP_RESOLUTION);
                            refetchArgs();
                        },
                    },
                );
            } else if (intentArg?.id) {
                handleQaApprove(true);
            }
        },
        [qaRejectMutation, setIsIntentChanged, setActiveStep, originalIntentArg],
    );

    if (intents.isFetching || ticketOfflineQuery.isFetching) {
        return <Typography color="disabled">Loading...</Typography>;
    }

    const isLocked = Boolean(selectedPolicySetId);

    return (
        <Root sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                sx={{
                    backgroundColor:
                        intentArg?.qa_at && !isIntentChanged
                            ? theme.palette.success.light
                            : isIntentChanged
                              ? theme.palette.warning.light
                              : 'transparent',
                    padding: 2,
                    borderRadius: 1,
                }}
            >
                <SearchAddIntent
                    disabled={isArgQueryLoading}
                    search={search}
                    onSearch={handleSearchChange}
                    onAdd={() => setSearch('')}
                />
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isArgQueryLoading || isIntentChanged || isLocked}
                    onClick={() => handleQaApprove()}
                >
                    Approve
                </Button>
            </Box>
            {isSearchModified && !isLocked && (
                <IntentsList
                    disabled={isArgQueryLoading}
                    expanded={Boolean(searchVal)}
                    intentsGrouped={intentGroups}
                    selectedIds={[]}
                    onIntentClick={onIntentSelect}
                />
            )}
        </Root>
    );
};

QaIntents.displayName = 'QaIntents';
